import React, { useState } from 'react';
import { 
  Card, 
  Row, 
  Col, 
  Typography, 
  Descriptions, 
  Tag, 
  Progress, 
  Statistic, 
  Button,
  Space,
  Modal,
  Form,
  Select,
  Input,
  InputNumber,
  message,
  Divider,
  Alert,
  Spin,
  DatePicker,
  Table,
} from 'antd';
import { 
  ArrowLeftOutlined,
  EditOutlined,
  CrownOutlined,
  TrophyOutlined,
  ShoppingOutlined,
  UserOutlined,
  ClockCircleOutlined,
  FileTextOutlined,
  MailOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { format } from 'date-fns';
import api, { adminApi, UserSubscription, ZarTransactionsResponse, NowPaymentsListResponse, AdminZarTxRequest, SubscriptionEventsResponse, SubscriptionEventItem } from '../lib/api';

const { Title, Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

const UserDetailsPage: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [form] = Form.useForm();

  // Filters and pagination state for reports
  const [actionFilter, setActionFilter] = useState<string>(''); // '', 'add_item', 'tryon', 'get_recom'
  const [actionsDateRange, setActionsDateRange] = useState<[string | undefined, string | undefined]>([undefined, undefined]);
  const [zarPagination, setZarPagination] = useState({ current: 1, pageSize: 10 });
  const [npPagination, setNpPagination] = useState({ current: 1, pageSize: 10 });
  const [zarDateRange, setZarDateRange] = useState<[string | undefined, string | undefined]>([undefined, undefined]);
  const [npDateRange, setNpDateRange] = useState<[string | undefined, string | undefined]>([undefined, undefined]);

  // Subscription history pagination/filter state
  const [subHistoryPagination, setSubHistoryPagination] = useState({ current: 1, pageSize: 10 });
  const [subHistoryDateRange, setSubHistoryDateRange] = useState<[string | undefined, string | undefined]>([undefined, undefined]);

  // Manual ZAR Transaction modal state
  const [txModalOpen, setTxModalOpen] = useState(false);
  const [txForm] = Form.useForm();

  const {
    data: userDetails,
    isLoading,
    error
  } = useQuery({
    queryKey: ['user-details', userId],
    queryFn: () => adminApi.getUserDetails(userId!),
    enabled: !!userId,
  });

  // Dedicated query for real-time subscription details (override, expiry, next tier)
  const {
    data: subscriptionDetail,
  } = useQuery<UserSubscription>({
    queryKey: ['user-subscription-detail', userId],
    queryFn: async () => {
      const resp = await api.get<UserSubscription>(`/admin/users/${userId}/subscription`);
      return resp.data;
    },
    enabled: !!userId,
    staleTime: 10_000,
  });

  const updateSubscriptionMutation = useMutation({
    mutationFn: ({
      tier,
      period,
      overrideDurationMinutes,
    }: {
      tier: string;
      period?: string;
      overrideDurationMinutes?: number;
    }) =>
      adminApi.updateUserSubscription(userId!, tier, period, false, overrideDurationMinutes),
    onSuccess: () => {
      message.success('User subscription updated successfully');
      setEditModalOpen(false);
      queryClient.invalidateQueries({ queryKey: ['user-details', userId] });
      queryClient.invalidateQueries({ queryKey: ['user-subscription-detail', userId] });
    },
    onError: () => {
      message.error('Failed to update user subscription');
    },
  });

  const getTierColor = (tier: string) => {
    const colors: Record<string, string> = {
      'free': 'default',
      'bronze': '#CD7F32',
      'silver': '#C0C0C0', 
      'gold': '#FFD700',
      'firooze': '#40E0D0'
    };
    return colors[tier] || 'default';
  };

  const getTierDisplayName = (tier: string) => {
    const names: Record<string, string> = {
      'free': 'Free',
      'bronze': 'Bronze',
      'silver': 'Silver',
      'gold': 'Gold',
      'firooze': 'Firooze'
    };
    return names[tier] || tier;
  };

  const handleEditSubscription = () => {
    if (userDetails) {
      const sub = subscriptionDetail ?? userDetails.subscription.user_subscription;
      form.setFieldsValue({
        tier: sub.tier,
        period: sub.period || 'monthly',
        duration_type: 'permanent',
        duration_value: undefined,
      });
      setEditModalOpen(true);
    }
  };

  const handleUpdateSubscription = (values: {
    tier: string;
    period: string;
    duration_type: string;
    duration_value?: number;
  }) => {
    let overrideDurationMinutes: number | undefined;
    if (values.duration_type && values.duration_type !== 'permanent' && values.duration_value) {
      const multipliers: Record<string, number> = {
        minutes: 1,
        hours: 60,
        days: 1440,
        weeks: 10080,
      };
      overrideDurationMinutes = values.duration_value * (multipliers[values.duration_type] ?? 1);
    }
    updateSubscriptionMutation.mutate({
      tier: values.tier,
      period: values.period,
      overrideDurationMinutes,
    });
  };

  // ---- User Actions Timeseries ----
  const actionsQuery = useQuery({
    queryKey: ['user-actions-timeseries', userId, actionFilter, actionsDateRange?.[0], actionsDateRange?.[1]],
    queryFn: async () => {
      const params: any = {};
      if (actionFilter) params.action = actionFilter;
      if (actionsDateRange?.[0]) params.start = actionsDateRange[0];
      if (actionsDateRange?.[1]) params.end = actionsDateRange[1];
      return adminApi.getUserActionsTimeseries(userId!, params);
    },
    enabled: !!userId,
    staleTime: 30_000,
  });

  // ---- ZAR Transactions ----
  const zarQuery = useQuery<ZarTransactionsResponse>({
    queryKey: ['user-zar-transactions', userId, zarPagination.current, zarPagination.pageSize, zarDateRange?.[0], zarDateRange?.[1]],
    queryFn: () => adminApi.getUserZarTransactions(userId!, {
      limit: zarPagination.pageSize,
      offset: (zarPagination.current - 1) * zarPagination.pageSize,
      start: zarDateRange?.[0],
      end: zarDateRange?.[1],
    }),
    enabled: !!userId,
    placeholderData: keepPreviousData,
  });

  // ---- NOWPayments ----
  const nowpaymentsQuery = useQuery<NowPaymentsListResponse>({
    queryKey: ['user-nowpayments', userId, npPagination.current, npPagination.pageSize, npDateRange?.[0], npDateRange?.[1]],
    queryFn: () => adminApi.getUserNowPayments(userId!, {
      limit: npPagination.pageSize,
      offset: (npPagination.current - 1) * npPagination.pageSize,
      start: npDateRange?.[0],
      end: npDateRange?.[1],
    }),
    enabled: !!userId,
    placeholderData: keepPreviousData,
  });

  // ---- Subscription Invoice History ----
  const subHistoryQuery = useQuery<SubscriptionEventsResponse>({
    queryKey: ['user-sub-history', userId, subHistoryPagination.current, subHistoryPagination.pageSize, subHistoryDateRange?.[0], subHistoryDateRange?.[1]],
    queryFn: () => adminApi.getUserSubscriptionHistory(userId!, {
      limit: subHistoryPagination.pageSize,
      skip: (subHistoryPagination.current - 1) * subHistoryPagination.pageSize,
      start_date: subHistoryDateRange?.[0],
      end_date: subHistoryDateRange?.[1],
    }),
    enabled: !!userId,
    placeholderData: keepPreviousData,
  });

  const calculateUsagePercentage = (used: number, limit?: number) => {
    if (!limit) return 0; // Unlimited
    return Math.min((used / limit) * 100, 100);
  };

  // ---- Manual ZAR Transaction mutation ----
  const confirmEmailMutation = useMutation({
    mutationFn: () => adminApi.confirmUserEmail(userId!),
    onSuccess: () => {
      message.success('Email confirmed successfully');
      queryClient.invalidateQueries({ queryKey: ['user-details', userId] });
    },
    onError: (e: any) => {
      message.error(e?.response?.data?.detail || e.message || 'Failed to confirm email');
    },
  });

  const createTxMutation = useMutation({
    mutationFn: async (values: { type: 'deposit'|'withdraw'; amount: number; reason?: string; metadata?: string }) => {
      const payload: AdminZarTxRequest = {
        type: values.type,
        amount: Number(values.amount),
        reason: values.reason || undefined,
        metadata: undefined,
      };
      if (values.metadata) {
        try {
          payload.metadata = JSON.parse(values.metadata);
        } catch {
          throw new Error('Invalid metadata JSON');
        }
      }
      return adminApi.createAdminZarTransaction(userId!, payload);
    },
    onSuccess: () => {
      message.success('Transaction created');
      setTxModalOpen(false);
      txForm.resetFields();
      // Refresh transactions list
      zarQuery.refetch();
    },
    onError: (e: any) => {
      message.error(e?.response?.data?.detail || e.message || 'Failed to create transaction');
    }
  });

  if (isLoading) {
    return (
      <div className="page-content" style={{ textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error || !userDetails) {
    return (
      <div className="page-content">
        <Alert
          message="Error loading user details"
          description="Could not load user information. Please try again."
          type="error"
          showIcon
        />
      </div>
    );
  }

  const { subscription, usage_today, closet } = userDetails;
  const plan = subscription.effective_plan;
  // Prefer the dedicated subscription query (has real billing_cycle_end, override fields)
  const userSub: UserSubscription = subscriptionDetail ?? subscription.user_subscription;

  // Simple inline bar chart component
  const SimpleBarChart: React.FC<{ data: any[]; valueKey: string; height?: number; color?: string }> = ({ data, valueKey, height = 140, color = '#1890ff' }) => {
    const max = Math.max(1, ...data.map((d: any) => Number(d[valueKey] || 0)));
    return (
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 4, height }}>
        {data.map((d: any, idx: number) => {
          const v = Number(d[valueKey] || 0);
          const h = Math.round((v / max) * (height - 24));
          return (
            <div key={idx} title={`${d.date}: ${v}`} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <div style={{ height: h, width: 10, background: color, borderRadius: 2 }} />
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="page-content">
      {/* Header */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col span={24}>
          <Space>
            <Button 
              icon={<ArrowLeftOutlined />} 
              onClick={() => navigate('/users')}
            >
              Back to Users
            </Button>
            <Title level={2} style={{ margin: 0 }}>
              User Details
            </Title>
          </Space>
        </Col>
      </Row>

      {/* User Information */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} lg={12}>
          <Card 
            title={
              <Space>
                <UserOutlined />
                User Information
              </Space>
            }
          >
            <Descriptions column={1} size="small">
              <Descriptions.Item label="User ID">
                <Text code>{userDetails.id}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Username">
                <Text strong>{userDetails.username}</Text>
              </Descriptions.Item>
              {userDetails.full_name && (
                <Descriptions.Item label="Full Name">
                  {userDetails.full_name}
                </Descriptions.Item>
              )}
              {userDetails.email && (
                <Descriptions.Item label="Email">
                  <Space>
                    {userDetails.email}
                    <Tag color={userDetails.email_verified ? 'green' : 'orange'} icon={userDetails.email_verified ? <CheckCircleOutlined /> : <MailOutlined />}>
                      {userDetails.email_verified ? 'Verified' : 'Unverified'}
                    </Tag>
                    {!userDetails.email_verified && (
                      <Button
                        type="primary"
                        size="small"
                        loading={confirmEmailMutation.isPending}
                        onClick={() => confirmEmailMutation.mutate()}
                      >
                        Confirm email
                      </Button>
                    )}
                  </Space>
                </Descriptions.Item>
              )}
              {userDetails.telegram_id && (
                <Descriptions.Item label="Telegram ID">
                  <Text code>{userDetails.telegram_id}</Text>
                </Descriptions.Item>
              )}
              <Descriptions.Item label="Status">
                <Tag color={userDetails.is_active ? 'green' : 'red'}>
                  {userDetails.is_active ? 'Active' : 'Inactive'}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Language">
                <Tag>{userDetails.language.toUpperCase()}</Tag>
              </Descriptions.Item>
              {userDetails.last_login && (
                <Descriptions.Item label="Last Activity">
                  {format(new Date(userDetails.last_login), 'MMMM dd, yyyy')}
                </Descriptions.Item>
              )}
            </Descriptions>
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card 
            title={
              <Space>
                <CrownOutlined />
                Subscription Details
                <Button 
                  type="link" 
                  size="small"
                  icon={<EditOutlined />}
                  onClick={handleEditSubscription}
                >
                  Edit
                </Button>
              </Space>
            }
          >
            <Space direction="vertical" style={{ width: '100%' }} size={8}>

              {/* ── Tier + period ─────────────────────────────────── */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Space>
                  <Tag
                    color={getTierColor(userSub.tier)}
                    icon={userSub.tier !== 'free' ? <CrownOutlined /> : undefined}
                    style={{ fontSize: 13, padding: '2px 10px' }}
                  >
                    {getTierDisplayName(userSub.tier)}
                  </Tag>
                  {userSub.period && <Tag>{userSub.period}</Tag>}
                </Space>
                <Tag color={userSub.is_active ? 'green' : 'red'}>
                  {userSub.is_active ? 'Active' : 'Inactive'}
                </Tag>
              </div>

              {/* ── Subscription period row ────────────────────────── */}
              {(() => {
                const expiryDate = userSub.override_until
                  ? new Date(userSub.override_until)
                  : (userSub.billing_cycle_end || userSub.current_period_end)
                    ? new Date((userSub.billing_cycle_end || userSub.current_period_end)!)
                    : null;
                const startDate = userSub.started_at ? new Date(userSub.started_at) : null;
                if (!startDate && !expiryDate) return null;
                const isPast = expiryDate && expiryDate < new Date();
                return (
                  <div style={{
                    background: '#fafafa',
                    border: '1px solid #f0f0f0',
                    borderRadius: 6,
                    padding: '8px 12px',
                  }}>
                    <Space split={<span style={{ color: '#bbb', margin: '0 4px' }}>→</span>} size={4}>
                      {startDate && (
                        <Space direction="vertical" size={0}>
                          <Text type="secondary" style={{ fontSize: 10 }}>STARTED</Text>
                          <Text style={{ fontSize: 13 }}>{format(startDate, 'MMM dd, yyyy')}</Text>
                        </Space>
                      )}
                      {expiryDate && (
                        <Space direction="vertical" size={0}>
                          <Text type="secondary" style={{ fontSize: 10 }}>
                            {userSub.override_until ? 'OVERRIDE EXPIRES' : 'EXPIRES'}
                          </Text>
                          <Text style={{ fontSize: 13, color: isPast ? '#ff4d4f' : '#52c41a' }}>
                            {format(expiryDate, 'MMM dd, yyyy HH:mm')}
                            {isPast ? ' ⚠' : ''}
                          </Text>
                        </Space>
                      )}
                    </Space>
                  </div>
                );
              })()}

              {/* ── Temporary override banner ──────────────────────── */}
              {userSub.override_until && (
                <Alert
                  type="warning"
                  showIcon
                  style={{ marginTop: 2 }}
                  message={<strong>Temporary admin override active</strong>}
                  description={
                    userSub.override_base_tier ? (
                      <>
                        After expiry, reverts to{' '}
                        <Tag color={getTierColor(userSub.override_base_tier)}>
                          {getTierDisplayName(userSub.override_base_tier)}
                        </Tag>
                        {userSub.override_base_period && (
                          <Tag>{userSub.override_base_period}</Tag>
                        )}
                      </>
                    ) : 'Will revert to base subscription after expiry.'
                  }
                />
              )}

              {/* ── Auto renew ────────────────────────────────────── */}
              <div>
                <Text type="secondary" style={{ fontSize: 12 }}>Auto Renew: </Text>
                <Tag color={userSub.auto_renew ? 'blue' : 'default'} style={{ fontSize: 11 }}>
                  {userSub.auto_renew ? 'Yes' : 'No'}
                </Tag>
              </div>

            </Space>
          </Card>
        </Col>
      </Row>

      {/* Subscription History */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col span={24}>
          <Card
            title={
              <Space>
                <FileTextOutlined />
                Subscription History
              </Space>
            }
            extra={
              <Space>
                <RangePicker
                  onChange={(dates) => {
                    if (dates) {
                      setSubHistoryDateRange([
                        dates[0]?.format('YYYY-MM-DD'),
                        dates[1]?.format('YYYY-MM-DD')
                      ]);
                      setSubHistoryPagination({ current: 1, pageSize: subHistoryPagination.pageSize });
                    } else {
                      setSubHistoryDateRange([undefined, undefined]);
                    }
                  }}
                />
                <Button onClick={() => subHistoryQuery.refetch()} loading={subHistoryQuery.isLoading}>Refresh</Button>
              </Space>
            }
          >
            <Table<SubscriptionEventItem>
              rowKey="id"
              loading={subHistoryQuery.isLoading}
              dataSource={subHistoryQuery.data?.events || []}
              pagination={{
                current: subHistoryPagination.current,
                pageSize: subHistoryPagination.pageSize,
                total: subHistoryQuery.data?.total || 0,
                showSizeChanger: true,
              }}
              onChange={(p) => setSubHistoryPagination({ current: p.current || 1, pageSize: p.pageSize || 10 })}
              locale={{ emptyText: 'No subscription history found' }}
              columns={[
                {
                  title: 'Date',
                  dataIndex: 'created_at',
                  key: 'created_at',
                  render: (v: string) => v ? format(new Date(v), 'MMM dd, yyyy HH:mm') : '-',
                },
                {
                  title: 'Event',
                  dataIndex: 'event_type',
                  key: 'event_type',
                  render: (v: string) => {
                    const cfg: Record<string, { color: string; label: string }> = {
                      created:        { color: 'blue',    label: 'Created' },
                      upgraded:       { color: 'green',   label: 'Upgraded' },
                      downgraded:     { color: 'orange',  label: 'Downgraded' },
                      cancelled:      { color: 'red',     label: 'Cancelled' },
                      expired:        { color: 'default', label: 'Expired' },
                      admin_override: { color: 'purple',  label: 'Admin Override' },
                    };
                    const { color, label } = cfg[v] ?? { color: 'default', label: v };
                    return <Tag color={color}>{label}</Tag>;
                  },
                },
                {
                  title: 'Actor',
                  dataIndex: 'actor',
                  key: 'actor',
                  render: (v: string) => {
                    const cfg: Record<string, { color: string }> = {
                      user:   { color: 'blue' },
                      admin:  { color: 'purple' },
                      system: { color: 'default' },
                    };
                    return <Tag color={cfg[v]?.color ?? 'default'}>{v?.toUpperCase()}</Tag>;
                  },
                },
                {
                  title: 'From Tier',
                  dataIndex: 'from_tier',
                  key: 'from_tier',
                  render: (v: string) => v ? (
                    <Tag color={getTierColor(v)}>{getTierDisplayName(v)}</Tag>
                  ) : '-',
                },
                {
                  title: 'To Tier',
                  dataIndex: 'to_tier',
                  key: 'to_tier',
                  render: (v: string) => v ? (
                    <Tag color={getTierColor(v)} icon={v !== 'free' ? <CrownOutlined /> : undefined}>
                      {getTierDisplayName(v)}
                    </Tag>
                  ) : '-',
                },
                {
                  title: 'Billing Period',
                  key: 'period',
                  render: (_: any, r: SubscriptionEventItem) => {
                    const period = r.to_period || r.from_period;
                    if (!period) return <Text type="secondary">—</Text>;
                    // Only show arrow when both sides exist AND they differ
                    if (r.from_period && r.to_period && r.from_period !== r.to_period) {
                      return (
                        <Space size={2}>
                          <Tag style={{ opacity: 0.6 }}>{r.from_period}</Tag>
                          <span style={{ color: '#999' }}>→</span>
                          <Tag>{r.to_period}</Tag>
                        </Space>
                      );
                    }
                    return <Tag>{period}</Tag>;
                  },
                },
                {
                  title: 'Valid Until',
                  key: 'valid_until',
                  render: (_: any, r: SubscriptionEventItem) => {
                    const dt = r.valid_until ?? r.metadata?.override_until ?? r.metadata?.billing_cycle_end;
                    if (!dt) return <Text type="secondary">—</Text>;
                    const date = new Date(dt);
                    const isPast = date < new Date();
                    return (
                      <Space direction="vertical" size={0}>
                        <Text style={{ fontSize: 12, color: isPast ? '#ff4d4f' : undefined }}>
                          {format(date, 'MMM dd, yyyy')}
                        </Text>
                        <Text type="secondary" style={{ fontSize: 11 }}>
                          {format(date, 'HH:mm')}
                          {isPast ? ' (expired)' : ''}
                        </Text>
                      </Space>
                    );
                  },
                },
                {
                  title: 'Reason',
                  dataIndex: 'reason',
                  key: 'reason',
                  render: (v: string) => v ? <Text type="secondary" style={{ fontSize: 12 }}>{v.replace(/_/g, ' ')}</Text> : <Text type="secondary">—</Text>,
                },
              ]}
            />
          </Card>
        </Col>
      </Row>

      {/* Today's Usage */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col span={24}>
          <Card 
            title={
              <Space>
                <ClockCircleOutlined />
                Today's Usage ({format(new Date(), 'MMM dd, yyyy')})
              </Space>
            }
          >
            <Row gutter={[24, 16]}>
              <Col xs={24} sm={8}>
                <div style={{ textAlign: 'center' }}>
                  <Statistic
                    title="Smart Add"
                    value={usage_today.smart_add_count}
                    suffix={plan.limits.max_smart_add_per_day ? `/ ${plan.limits.max_smart_add_per_day}` : ''}
                  />
                  {plan.limits.max_smart_add_per_day && (
                    <Progress
                      percent={calculateUsagePercentage(usage_today.smart_add_count, plan.limits.max_smart_add_per_day)}
                      size="small"
                      status={usage_today.smart_add_count >= plan.limits.max_smart_add_per_day ? 'exception' : 'normal'}
                    />
                  )}
                </div>
              </Col>
              <Col xs={24} sm={8}>
                <div style={{ textAlign: 'center' }}>
                  <Statistic
                    title="Try-ons"
                    value={usage_today.try_on_count}
                    suffix={plan.limits.max_try_on_per_day ? `/ ${plan.limits.max_try_on_per_day}` : ''}
                  />
                  {plan.limits.max_try_on_per_day && (
                    <Progress
                      percent={calculateUsagePercentage(usage_today.try_on_count, plan.limits.max_try_on_per_day)}
                      size="small"
                      status={usage_today.try_on_count >= plan.limits.max_try_on_per_day ? 'exception' : 'normal'}
                    />
                  )}
                </div>
              </Col>
              <Col xs={24} sm={8}>
                <div style={{ textAlign: 'center' }}>
                  <Statistic
                    title="AI Stylist"
                    value={usage_today.ai_stylist_count}
                    suffix={plan.limits.max_ai_stylist_per_day ? `/ ${plan.limits.max_ai_stylist_per_day}` : ''}
                  />
                  {plan.limits.max_ai_stylist_per_day && (
                    <Progress
                      percent={calculateUsagePercentage(usage_today.ai_stylist_count, plan.limits.max_ai_stylist_per_day)}
                      size="small"
                      status={usage_today.ai_stylist_count >= plan.limits.max_ai_stylist_per_day ? 'exception' : 'normal'}
                    />
                  )}
                </div>
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>

      {/* Plan Limits and Features */}
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <Card 
            title={
              <Space>
                <TrophyOutlined />
                Plan Limits
              </Space>
            }
          >
            <Space direction="vertical" style={{ width: '100%' }}>
              <div>
                <Text strong>Closet Items: </Text>
                <Text>
                  {closet.total_items} / {plan.limits.max_closet_items || '∞'}
                </Text>
                {plan.limits.max_closet_items && (
                  <Progress
                    percent={calculateUsagePercentage(closet.total_items, plan.limits.max_closet_items)}
                    size="small"
                    style={{ marginTop: 4 }}
                  />
                )}
              </div>
              
              <Divider style={{ margin: '8px 0' }} />
              
              <div>
                <Text strong>Daily Limits:</Text>
                <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                  <li>Smart Add: {plan.limits.max_smart_add_per_day || 'Unlimited'}</li>
                  <li>Try-ons: {plan.limits.max_try_on_per_day || 'Unlimited'}</li>
                  <li>AI Stylist: {plan.limits.max_ai_stylist_per_day || 'Unlimited'}</li>
                </ul>
              </div>

              <div>
                <Text strong>Try-on Limits:</Text>
                <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                  <li>Items per try-on: {plan.limits.cloth_item_per_tryon || 'Unlimited'}</li>
                </ul>
              </div>
            </Space>
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card 
            title={
              <Space>
                <ShoppingOutlined />
                Available Features
              </Space>
            }
          >
            <Space direction="vertical" style={{ width: '100%' }}>
              <div>
                <Text strong>AI Features:</Text>
                <div style={{ marginTop: 8 }}>
                  <Tag color={plan.limits.quick_ai_stylist ? 'green' : 'red'}>
                    Quick AI Stylist: {plan.limits.quick_ai_stylist ? 'Yes' : 'No'}
                  </Tag>
                </div>
                <div style={{ marginTop: 4 }}>
                  <Tag color={plan.limits.advanced_ai_stylist ? 'green' : 'red'}>
                    Advanced AI Stylist: {plan.limits.advanced_ai_stylist ? 'Yes' : 'No'}
                  </Tag>
                </div>
              </div>

              <Divider style={{ margin: '8px 0' }} />

              <div>
                <Text strong>Try-on Features:</Text>
                <div style={{ marginTop: 8 }}>
                  <Tag color={plan.limits.custom_prompt_in_try_on ? 'green' : 'red'}>
                    Custom Prompts: {plan.limits.custom_prompt_in_try_on ? 'Yes' : 'No'}
                  </Tag>
                </div>
              </div>
            </Space>
          </Card>
        </Col>
      </Row>

      {/* User Actions Timeseries */}
      <Row gutter={[16, 16]} style={{ marginTop: 24 }}>
        <Col span={24}>
          <Card
            title={
              <Space>
                <ClockCircleOutlined />
                User Actions Over Time
              </Space>
            }
            extra={
              <Space>
                <Select
                  placeholder="Action"
                  value={actionFilter || undefined}
                  onChange={(v) => setActionFilter(v || '')}
                  allowClear
                  style={{ width: 180 }}
                >
                  <Option value="add_item">Add Item</Option>
                  <Option value="tryon">Try-on</Option>
                  <Option value="get_recom">Get Recommendations</Option>
                </Select>
                <RangePicker
                  onChange={(dates) => {
                    if (dates) {
                      setActionsDateRange([
                        dates[0]?.format('YYYY-MM-DD'),
                        dates[1]?.format('YYYY-MM-DD')
                      ]);
                    } else {
                      setActionsDateRange([undefined, undefined]);
                    }
                  }}
                />
                <Button onClick={() => actionsQuery.refetch()} loading={actionsQuery.isLoading}>Refresh</Button>
              </Space>
            }
          >
            {actionsQuery.isError && (
              <Alert type="error" message="Failed to load actions timeseries" style={{ marginBottom: 16 }} />
            )}

            {actionsQuery.data && (
              <div>
                {!actionFilter ? (
                  <div style={{ display: 'grid', gap: 16 }}>
                    <div>
                      <Text strong>Add Item</Text>
                      <SimpleBarChart data={actionsQuery.data.series} valueKey="add_item" color="#52c41a" />
                    </div>
                    <div>
                      <Text strong>Try-on</Text>
                      <SimpleBarChart data={actionsQuery.data.series} valueKey="tryon" color="#722ed1" />
                    </div>
                    <div>
                      <Text strong>Get Recommendations</Text>
                      <SimpleBarChart data={actionsQuery.data.series} valueKey="get_recom" color="#fa8c16" />
                    </div>
                  </div>
                ) : (
                  <div>
                    <SimpleBarChart data={actionsQuery.data.series} valueKey="count" color="#1890ff" />
                  </div>
                )}
              </div>
            )}
          </Card>
        </Col>
      </Row>

      {/* Wallet Transactions and Payments */}
      <Row gutter={[16, 16]} style={{ marginTop: 24 }}>
        <Col xs={24} lg={12}>
          <Card title="ZAR Wallet Transactions" extra={
            <Space>
              <Button type="primary" onClick={() => setTxModalOpen(true)}>Manual Transaction</Button>
              <RangePicker
                onChange={(dates) => {
                  if (dates) {
                    setZarDateRange([
                      dates[0]?.format('YYYY-MM-DD'),
                      dates[1]?.format('YYYY-MM-DD')
                    ]);
                    setZarPagination({ current: 1, pageSize: zarPagination.pageSize });
                  } else {
                    setZarDateRange([undefined, undefined]);
                  }
                }}
              />
              <Button onClick={() => zarQuery.refetch()} loading={zarQuery.isLoading}>Refresh</Button>
            </Space>
          }>
            <Table
              rowKey={(r: any) => r.id || `${r.created_at}-${r.amount}`}
              loading={zarQuery.isLoading}
              dataSource={zarQuery.data?.items || []}
              pagination={{
                current: zarPagination.current,
                pageSize: zarPagination.pageSize,
                total: zarQuery.data?.total || 0,
                showSizeChanger: true,
              }}
              onChange={(p) => setZarPagination({ current: p.current || 1, pageSize: p.pageSize || 10 })}
              columns={[
                { title: 'Date', dataIndex: 'created_at', key: 'created_at', render: (v: string) => v ? format(new Date(v), 'MMM dd, yyyy HH:mm') : '-' },
                { title: 'Type', dataIndex: 'type', key: 'type', render: (v: string) => <Tag>{v}</Tag> },
                { title: 'Amount', dataIndex: 'amount', key: 'amount', render: (v: number, r: any) => <Text strong>{v} {r.currency || 'ZAR'}</Text> },
                { title: 'Reason', dataIndex: 'reason', key: 'reason' },
              ]}
            />
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card title="NOWPayments Payments" extra={
            <Space>
              <RangePicker
                onChange={(dates) => {
                  if (dates) {
                    setNpDateRange([
                      dates[0]?.format('YYYY-MM-DD'),
                      dates[1]?.format('YYYY-MM-DD')
                    ]);
                    setNpPagination({ current: 1, pageSize: npPagination.pageSize });
                  } else {
                    setNpDateRange([undefined, undefined]);
                  }
                }}
              />
              <Button onClick={() => nowpaymentsQuery.refetch()} loading={nowpaymentsQuery.isLoading}>Refresh</Button>
            </Space>
          }>
            <Table
              rowKey={(r: any) => r.id || r.order_id}
              loading={nowpaymentsQuery.isLoading}
              dataSource={nowpaymentsQuery.data?.items || []}
              pagination={{
                current: npPagination.current,
                pageSize: npPagination.pageSize,
                total: nowpaymentsQuery.data?.total || 0,
                showSizeChanger: true,
              }}
              onChange={(p) => setNpPagination({ current: p.current || 1, pageSize: p.pageSize || 10 })}
              columns={[
                { title: 'Date', dataIndex: 'created_at', key: 'created_at', render: (v: string) => v ? format(new Date(v), 'MMM dd, yyyy HH:mm') : '-' },
                { title: 'Type', dataIndex: 'type', key: 'type', render: (v: string) => <Tag color={v === 'subscription' ? 'gold' : 'blue'}>{v}</Tag> },
                { title: 'Status', dataIndex: 'status', key: 'status', render: (v: string) => <Tag color={v?.includes('finish') || v?.includes('confirm') ? 'green' : (v?.includes('fail') || v?.includes('cancel') || v?.includes('expire')) ? 'red' : 'default'}>{v || '-'}</Tag> },
                { title: 'Price', key: 'price', render: (_: any, r: any) => r.price_amount ? `${r.price_amount} ${r.price_currency || 'USD'}` : '-' },
                { title: 'Pay Currency', dataIndex: 'pay_currency', key: 'pay_currency' },
                { title: 'Order ID', dataIndex: 'order_id', key: 'order_id' },
                { title: 'Invoice', dataIndex: 'invoice_url', key: 'invoice_url', render: (url: string) => url ? <a href={url} target="_blank" rel="noreferrer">Open</a> : '-' },
              ]}
            />
          </Card>
        </Col>
      </Row>

      {/* Manual ZAR Transaction Modal */}
      <Modal
        title="Manual ZAR Transaction"
        open={txModalOpen}
        onCancel={() => setTxModalOpen(false)}
        onOk={() => txForm.submit()}
        confirmLoading={createTxMutation.isPending}
      >
        <Form form={txForm} layout="vertical" onFinish={(vals) => createTxMutation.mutate(vals)}>
          <Form.Item name="type" label="Type" rules={[{ required: true }]}> 
            <Select placeholder="Select type">
              <Option value="deposit">Deposit</Option>
              <Option value="withdraw">Withdraw</Option>
            </Select>
          </Form.Item>
          <Form.Item name="amount" label="Amount (ZAR)" rules={[{ required: true, message: 'Enter amount' }]}> 
            <InputNumber min={1} step={1} precision={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="reason" label="Reason">
            <Input placeholder="Optional" />
          </Form.Item>
          <Form.Item name="metadata" label="Metadata (JSON)">
            <Input.TextArea placeholder='{"source":"admin"}' rows={4} />
          </Form.Item>
        </Form>
      </Modal>

      {/* Edit Subscription Modal */}
      <Modal
        title="Edit User Subscription"
        open={editModalOpen}
        onCancel={() => setEditModalOpen(false)}
        onOk={() => form.submit()}
        confirmLoading={updateSubscriptionMutation.isPending}
        width={480}
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{ duration_type: 'permanent' }}
          onFinish={handleUpdateSubscription}
        >
          {/* Show active temporary override badge */}
          {userSub?.override_until && (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
              message={
                <>
                  Active temporary override — expires{' '}
                  <strong>
                    {new Date(userSub.override_until).toLocaleString()}
                  </strong>
                  . Setting a new override will replace it.
                </>
              }
            />
          )}

          <Form.Item
            name="tier"
            label="Subscription Tier"
            rules={[{ required: true, message: 'Please select a subscription tier' }]}
          >
            <Select placeholder="Select subscription tier">
              <Option value="free">Free</Option>
              <Option value="bronze">Bronze</Option>
              <Option value="silver">Silver</Option>
              <Option value="gold">Gold</Option>
              <Option value="firooze">Firooze</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="period"
            label="Billing Period"
            rules={[{ required: true, message: 'Please select a billing period' }]}
          >
            <Select placeholder="Select billing period">
              <Option value="monthly">Monthly</Option>
              <Option value="annual">Annual</Option>
            </Select>
          </Form.Item>

          <Form.Item label="Override Duration">
            <Input.Group compact>
              <Form.Item name="duration_type" noStyle initialValue="permanent">
                <Select style={{ width: '45%' }}>
                  <Option value="permanent">Permanent</Option>
                  <Option value="minutes">Minutes</Option>
                  <Option value="hours">Hours</Option>
                  <Option value="days">Days</Option>
                  <Option value="weeks">Weeks</Option>
                </Select>
              </Form.Item>
              <Form.Item
                noStyle
                shouldUpdate={(prev, cur) => prev.duration_type !== cur.duration_type}
              >
                {({ getFieldValue }) =>
                  getFieldValue('duration_type') !== 'permanent' ? (
                    <Form.Item
                      name="duration_value"
                      noStyle
                      rules={[{ required: true, message: 'Enter a value' }]}
                    >
                      <InputNumber
                        min={1}
                        style={{ width: '55%' }}
                        placeholder={`Number of ${getFieldValue('duration_type')}`}
                      />
                    </Form.Item>
                  ) : null
                }
              </Form.Item>
            </Input.Group>
            <div style={{ color: '#888', fontSize: 12, marginTop: 4 }}>
              Leave "Permanent" for a standard admin override with no expiry. Choose a unit +
              value for a time-limited grant (e.g. 1 day, 2 hours).
            </div>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default UserDetailsPage;